export type EarningsDeltaStatus = 'beat' | 'inline' | 'miss' | 'neutral';

export interface EarningsComparison {
  text: string;
  status: EarningsDeltaStatus;
}

export interface EarningsMetric {
  label: string;
  value: string;
  tooltip: string;
  primary: EarningsComparison;
  /** A second badge, or supporting facts shown one per line. */
  secondary?: EarningsComparison | string[];
  note?: string;
  /** Adds the shared non-GAAP explanation to this metric's tooltip. */
  nonGaap?: boolean;
}

export interface EarningsGuidanceItem {
  text: string;
  raised?: boolean;
}

export interface QuarterEarnings {
  fiscalYear: number;
  quarter: 1 | 2 | 3 | 4;
  quarterLabel: string;
  /** ISO datetime; the quarter stays hidden until its release is public. */
  publishAfter: string;
  releaseUrl: string;
  callReplayUrl: string;
  metrics: EarningsMetric[];
  guidance: {period: string; items: EarningsGuidanceItem[]}[];
}

/** Figures from the public Q2 2026 earnings release. */
const Q2_2026: QuarterEarnings = {
  fiscalYear: 2026,
  quarter: 2,
  quarterLabel: 'Q2 2026 results',
  publishAfter: '2026-08-06T20:05:00Z',
  releaseUrl: 'https://investors.dropbox.com/',
  callReplayUrl: 'https://investors.dropbox.com/',
  metrics: [
    {
      label: 'Revenue',
      value: '$631.5M',
      tooltip:
        "Total subscription revenue recognized in the quarter. 'Ex-FormSwift' excludes a product we're winding down, which shows the trend in our ongoing business.",
      primary: {text: 'Beat high end of $624–627M guide by $4.5M', status: 'beat'},
      secondary: ['+0.9% YoY', '+1.7% ex-FormSwift', 'Consensus $626.4M'],
    },
    {
      label: 'Total ARR',
      value: '$2.566B',
      tooltip:
        "Annual Recurring Revenue: the number of users with active paid licenses multiplied by their annualized subscription price. It's our best early read on future revenue. 'Constant currency' removes the effect of exchange-rate swings.",
      primary: {text: '+1.0% YoY', status: 'neutral'},
      secondary: [
        '+1.7% ex-FormSwift',
        '+0.2% constant currency',
        '+$11.0M QoQ (cc, ex-FormSwift)',
      ],
    },
    {
      label: 'Paying users',
      value: '18.19M',
      tooltip: 'Users with an active paid subscription at quarter end.',
      primary: {text: '+96K QoQ', status: 'beat'},
      secondary: ['vs. 18.13M last year', '3rd straight quarter of growth'],
    },
    {
      label: 'Unlevered free cash flow',
      value: '$283.5M',
      tooltip:
        'Cash generated by the business before interest payments on debt, after capital spending. It funds share buybacks and debt paydown.',
      primary: {text: 'vs. $276.4M last year', status: 'beat'},
      secondary: ['FY26 guide raised to ≥$1.070B'],
    },
    {
      label: 'ARPU',
      value: '$139.68',
      tooltip:
        'Average revenue per paying user, annualized. Expected to dip as lower-priced plans grow and FormSwift winds down.',
      primary: {text: 'vs. $138.32 last year', status: 'neutral'},
      note: 'Management expects modest sequential declines in 2026',
    },
    {
      label: 'Non-GAAP operating margin',
      nonGaap: true,
      value: '39.7%',
      tooltip:
        'Operating profit as a share of revenue, excluding items like stock-based compensation. Our main profitability measure.',
      primary: {text: '+120 bps vs. ~38.5% guide', status: 'beat'},
      secondary: {text: '−180 bps YoY', status: 'miss'},
    },
    {
      label: 'Non-GAAP gross margin',
      nonGaap: true,
      value: '81.6%',
      tooltip:
        'Revenue minus the cost of delivering our service (infrastructure, support), as a percentage of revenue. AI compute costs are a current pressure.',
      primary: {text: 'In line with FY26 guide ~81.5%', status: 'inline'},
      secondary: ['vs. 82.2% last year'],
    },
    {
      label: 'Non-GAAP EPS',
      nonGaap: true,
      value: '$0.75',
      tooltip:
        'Earnings per diluted share, excluding certain items. Share buybacks reduce share count, which lifts EPS.',
      primary: {text: '+$0.01 vs. consensus $0.74', status: 'beat'},
      secondary: ['+5.6% YoY', 'GAAP EPS $0.42'],
    },
  ],
  guidance: [
    {
      period: 'Q3 2026',
      items: [{text: 'Revenue $627–630M'}, {text: 'Non-GAAP op margin ~38.5%'}],
    },
    {
      period: 'FY 2026',
      items: [
        {text: 'Revenue $2.513–2.523B', raised: true},
        {text: 'Non-GAAP op margin 40.0–40.5%', raised: true},
        {text: 'Gross margin ~81.5%'},
        {text: 'Unlevered FCF ≥$1.070B', raised: true},
      ],
    },
  ],
};

/**
 * Every quarter we have figures for. Add each new release here (any order);
 * it appears in the tile once its `publishAfter` time passes.
 */
export const EARNINGS_QUARTERS: QuarterEarnings[] = [Q2_2026];

export const quarterId = (q: QuarterEarnings): string => `${q.fiscalYear}-q${q.quarter}`;

/**
 * Published quarters for the selector, newest first. Only the latest fiscal
 * year is offered, so the list resets when the first quarter of a new year
 * is released.
 */
export const getSelectableQuarters = (
  now: Date = new Date(),
  quarters: QuarterEarnings[] = EARNINGS_QUARTERS
): QuarterEarnings[] => {
  const published = quarters
    .filter((q) => now >= new Date(q.publishAfter))
    .sort((a, b) => b.fiscalYear - a.fiscalYear || b.quarter - a.quarter);
  const latestYear = published[0]?.fiscalYear;
  return published.filter((q) => q.fiscalYear === latestYear);
};
